import { pageFaqAdditions } from "@/data/pageFaqs";
import { PageFaqs } from "@/components/site/PageFaqs";
import { createFileRoute, Link } from "@tanstack/react-router";
import { BookFitCall } from "@/components/site/BookFitCall";
import { fitCallUrl } from "@/lib/booking";
import {
  breadcrumbSchema,
  canonicalLinks,
  jsonLd,
  ogImageUrl,
  ogUrl,
  faqPageSchema, webPageSchema,
} from "@/lib/seo";
import { ArrowUpRight } from "lucide-react";
import { SITE_URL } from "@/lib/site";
import { careerEmployers } from "@/data/clients";
import { NextStep } from "@/components/site/NextStep";


const TITLE = "Adam Ayoub, Fractional COO of Ajility";
const DESCRIPTION =
  "Adam Ayoub is the Fractional COO of Ajility, with more than fifteen years in general manager and COO roles across Australian industry.";

// The Person node (#person-adam) is declared once in the site-wide root graph.


const faqs = pageFaqAdditions["/about/adam-ayoub"];

export const Route = createFileRoute("/about_/adam-ayoub")({
  head: () => ({
    meta: [
      { name: "twitter:card", content: "summary_large_image" },
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "profile" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
      { property: "og:image", content: `${SITE_URL}/images/adam-ayoub.jpg` },
      { property: "og:image:secure_url", content: `${SITE_URL}/images/adam-ayoub.jpg` },
      { property: "og:image:type", content: "image/jpeg" },
      { property: "og:image:width", content: "800" },
      { property: "og:image:height", content: "1000" },
      { property: "og:image:alt", content: "Adam Ayoub, Fractional COO and Integrator, Ajility" },
      { name: "twitter:image", content: `${SITE_URL}/images/adam-ayoub.jpg` },
      { name: "twitter:image:alt", content: "Adam Ayoub, Fractional COO and Integrator, Ajility" },
      // Second og:image (the social card). TanStack dedupes meta by name ?? property,
      // so a distinct name keeps this tag alongside the portrait above.
      { name: "og:image:card", property: "og:image", content: ogImageUrl("about-adam-ayoub") },
      ogUrl("/about/adam-ayoub"),
    ],
    links: canonicalLinks("/about/adam-ayoub"),
    scripts: [
      jsonLd(faqPageSchema("/about/adam-ayoub", faqs)),
      jsonLd(
        breadcrumbSchema([
          { name: "About", path: "/about" },
          { name: "Adam Ayoub", path: "/about/adam-ayoub" },
        ]),
      ),
      jsonLd(
        webPageSchema({ image: "about-adam-ayoub",
          path: "/about/adam-ayoub",
          name: TITLE,
          description: DESCRIPTION,
          type: "ProfilePage",
        }),
      ),
    ],
  }),
  component: AdamPage,
});

const industries = [
  "Mining services",
  "Transport and logistics",
  "Construction and contracting",
  "Electrical and trade services",
  "Healthcare",
  "Professional services",
  "Retail",
  "Workplace technology",
];

const ajilityEngagements = [
  {
    label: "Mining services",
    title: "About $1M of annualised overhead removed in 90 days",
    body: "A commercial reset in the first 90 days of the engagement removed roughly $1M of annualised overhead and secured a multi-million-dollar contract variation that had been sitting unclaimed.",
    to: "/insights/$slug" as const,
    slug: "mining-90-days",
  },
];

const careerEngagements = [
  {
    label: "Transport and logistics",
    title: "118 per cent net profit growth over four years",
    body: "A freight business was growing revenue quickly and losing the margin on the way through. Four operating levers, applied in sequence, turned that growth into 118 per cent net profit growth over four years.",
    to: "/insights/$slug" as const,
    slug: "transport-118-percent",
  },
  {
    label: "Electrical contracting",
    title: "Project-level financial discipline rebuilt margins",
    body: "The business won plenty of work but could not say which jobs made money. Job-level costing, a monthly commercial review and pricing discipline made margin visible before a job closed rather than after it.",
    to: "/insights/$slug" as const,
    slug: "electrical-margins",
  },
  {
    label: "Healthcare",
    title: "A clinician-founded business given an operator",
    body: "Vision and momentum were never the issue. Installing the Integrator role, a weekly leadership cadence and owned quarterly priorities turned strategy into something the team could actually execute.",
    to: "/insights/$slug" as const,
    slug: "healthcare-integrator",
  },
];

const careerBrands = careerEmployers.map((c) => ({
  name: c.name,
  logo: c.logo,
  role: c.role ?? "Senior leadership",
}));


function AdamPage() {
  return (
    <>
      <section className="relative overflow-hidden bg-midnight-deep pt-40 pb-24 text-frost">
        <div className="absolute inset-0 grid-lines opacity-20" />
        <div className="relative mx-auto grid max-w-6xl gap-12 px-5 sm:px-8 lg:grid-cols-[1.4fr_1fr] lg:items-center">
          <div>
            <span className="eyebrow on-dark">Author and Fractional COO</span>
            <h1 className="mt-6 font-serif text-4xl leading-[1.05] sm:text-5xl lg:text-6xl text-balance">
              Adam Ayoub
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-frost/75">
              Fractional COO of Ajility and the operator behind every engagement. More than fifteen
              years in general manager and chief operating officer roles, running the parts of
              a business that decide whether a plan turns into a result.
            </p>
            <div className="mt-8 flex flex-wrap items-start gap-3">
              <BookFitCall
                placement="about_adam_hero"
                buttonClassName="inline-flex items-center gap-2 rounded-full bg-frost px-6 py-3 text-sm font-medium text-midnight-deep transition hover:bg-white"
                noteClassName="mt-2 text-sm font-medium text-frost/70 hover:text-frost"
              >
                Book a fit call <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
              </BookFitCall>
              <a
                href="https://www.linkedin.com/in/adamayoub/"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 text-sm font-medium text-frost transition hover:border-frost"
              >
                Connect on LinkedIn
              </a>
            </div>
          </div>
          <div className="justify-self-start lg:justify-self-end">
            <img
              src="/images/adam-ayoub.jpg"
              alt="Adam Ayoub, Fractional COO of Ajility"
              width={800}
              height={1000}
              loading="eager"
              fetchPriority="high"
              decoding="async"
              className="h-56 w-56 rounded-3xl object-cover sm:h-72 sm:w-72"
            />
          </div>
        </div>
      </section>

      <section className="bg-background py-24">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <h2 className="font-serif text-3xl text-foreground">Background</h2>
          <div className="mt-6 space-y-5 text-lg leading-relaxed text-charcoal">
            <p>
              Adam Ayoub has spent more than fifteen years as a general manager and chief
              operating officer inside businesses where the operating pressure is real: fleets
              to run, crews on site, contracts to deliver and margin that disappears quietly if
              nobody watches it. That work covered mining services, transport and logistics,
              construction and contracting, healthcare, retail and professional services.
            </p>
            <p>
              The pattern repeated in every one of them. The strategy was rarely the problem.
              The gap sat between the plan and the week: no owned priorities, no cadence that
              held people to them, and numbers that arrived too late to change a decision.
              Closing that gap is the whole of the job.
            </p>
            <p>
              Ajility exists to do that work directly for founder-led Australian
              businesses, typically between $3M and $30M in revenue, where growth has outpaced
              structure. Ajility delivers every engagement personally. There are no juniors and no
              handovers, and the{" "}
              <Link to="/packages" className="font-medium text-midnight underline decoration-steel underline-offset-4">
                price for each piece of work is published
              </Link>
              .
            </p>
          </div>

          <h2 className="mt-16 font-serif text-3xl text-foreground">Industries</h2>
          <ul className="mt-6 flex flex-wrap gap-2">
            {industries.map((industry) => (
              <li
                key={industry}
                className="rounded-full border border-border bg-card px-4 py-2 text-sm text-charcoal"
              >
                {industry}
              </li>
            ))}
          </ul>

          <h2 className="mt-16 font-serif text-2xl text-foreground sm:text-3xl whitespace-nowrap">Ajility engagements</h2>
          <p className="mt-4 text-charcoal">
            These engagements are delivered by Adam through Ajility, where he holds the Fractional COO and Integrator seat.
          </p>
          <div className="mt-8 space-y-6">
            {ajilityEngagements.map((item) => (
              <article key={item.slug} className="card-panel">
                <span className="eyebrow text-steel">{item.label}</span>
                <h3 className="mt-3 font-serif text-xl text-foreground">{item.title}</h3>
                <p className="mt-3 text-charcoal">{item.body}</p>
                <Link
                  to={item.to}
                  params={{ slug: item.slug }}
                  className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-midnight hover:underline"
                >
                  {item.title} <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </article>
            ))}
          </div>

          <h2 className="mt-16 font-serif text-3xl text-foreground">Operating career</h2>
          <p className="mt-4 text-charcoal">
            These outcomes come from Adam's operating career before Ajility, delivered inside the businesses themselves.
          </p>
          <div className="mt-8 space-y-6">
            {careerEngagements.map((item) => (
              <article key={item.slug} className="card-panel">
                <span className="eyebrow text-steel">{item.label}</span>
                <h3 className="mt-3 font-serif text-xl text-foreground">{item.title}</h3>
                <p className="mt-3 text-charcoal">{item.body}</p>
                <Link
                  to={item.to}
                  params={{ slug: item.slug }}
                  className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-midnight hover:underline"
                >
                  {item.title} <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </article>
            ))}
          </div>

          <h3 className="mt-12 text-sm font-semibold uppercase tracking-widest text-steel">
            Career
          </h3>
          <ul className="mt-4 flex flex-wrap items-center gap-3" aria-label="Businesses from Adam's operating career">
            {careerBrands.map((brand) => (
              <li
                key={brand.name}
                className="flex flex-col items-start justify-center rounded-full border border-border bg-card px-4 py-2 text-sm text-charcoal"
              >
                <span className="text-[11px] font-semibold uppercase tracking-widest text-charcoal leading-tight">
                  {brand.role}
                </span>
                {brand.logo ? (
                  <img
                    src={brand.logo}
                    alt={`${brand.name} logo`}
                    loading="lazy"
                    width={360}
                    height={120}
                    className="mt-1 h-9 w-auto max-w-[10rem] object-contain"
                  />
                ) : (
                  <span className="mt-1">{brand.name}</span>
                )}
              </li>
            ))}
          </ul>


          <h2 className="mt-16 font-serif text-3xl text-foreground">How Ajility works</h2>
          <div className="mt-6 space-y-5 text-lg leading-relaxed text-charcoal">
            <p>
              Every engagement runs to the same sequence, from the first enquiry to handover.
              Scope and price come from the published catalogue, sessions are booked for the
              full engagement rather than the first fortnight, and every action carries a named
              owner and a date. The detail is set out in{" "}
              <Link to="/how-we-work" className="font-medium text-midnight underline decoration-steel underline-offset-4">
                how we work
              </Link>
              .
            </p>
            <p>
              Ajility works on site in Sydney, on the Central Coast, in Newcastle, Melbourne and
              Perth, and remotely with founders elsewhere in Australia. He writes the{" "}
              <Link to="/insights" className="font-medium text-midnight underline decoration-steel underline-offset-4">
                Ajility operating insights
              </Link>
              , which are drawn from live engagements rather than theory.
            </p>
          </div>
        </div>
      </section>

      <PageFaqs faqs={faqs} />

      <NextStep
        heading="Work with Ajility directly"
        body="Every engagement is led by Ajility. Start with the free Operating Scan, or book a 30-minute fit call to talk it through."
        primaryLabel="Take the free Operating Scan"
        primaryTo="/scan"
        secondaryLabel="Book a fit call"
        secondaryTo={fitCallUrl("about_adam_footer")}
        secondaryNote={
          <Link to="/contact" className="text-sm font-medium text-frost/70 hover:text-frost">
            Or send a note instead
          </Link>
        }
      />
    </>
  );
}
